import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Trash2, Calendar, CreditCard, Info, AlertTriangle, Settings, X, Volume2, VolumeX, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { notificationsApi } from '../lib/api';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const previousUnreadCountRef = useRef(null);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return localStorage.getItem('resawork-notif-sound') !== '0'; } catch (_e) { return true; }
  });
  // When a grouped notification is expanded we show the individual titles
  // inline so the user still gets the details without leaving the dropdown.
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroupExpanded = (id, e) => {
    e?.stopPropagation?.();
    setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Play a short pleasant "ping" using Web Audio API (no asset to load).
  const playPingSound = () => {
    try {
      if (!soundEnabled) return;
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      // Two-tone ding (E5 then G5) for a softer notification feel
      const tones = [
        { freq: 660, start: 0,    duration: 0.18 },
        { freq: 880, start: 0.12, duration: 0.22 },
      ];
      tones.forEach(({ freq, start, duration }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, ctx.currentTime + start);
        gain.gain.linearRampToValueAtTime(0.22, ctx.currentTime + start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + duration);
        osc.connect(gain).connect(ctx.destination);
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + start + duration + 0.05);
      });
      // Close context after the ding finishes
      setTimeout(() => { try { ctx.close(); } catch (_e) { /* noop */ } }, 800);
    } catch (_e) {
      // ignore — sound is non-critical
    }
  };

  const toggleSound = (e) => {
    e?.stopPropagation?.();
    const newVal = !soundEnabled;
    setSoundEnabled(newVal);
    try { localStorage.setItem('resawork-notif-sound', newVal ? '1' : '0'); } catch (_e) { /* noop */ }
    if (newVal) playPingSound();
  };

  // Fetch unread count on mount and periodically
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationsApi.getUnreadCount();
      const newCount = response.data.unread_count;
      // Play sound when count increases (skip first call on mount)
      if (previousUnreadCountRef.current !== null && newCount > previousUnreadCountRef.current) {
        playPingSound();
      }
      previousUnreadCountRef.current = newCount;
      setUnreadCount(newCount);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await notificationsApi.getAll();
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBellClick = () => {
    if (!isOpen) {
      fetchNotifications();
    }
    setIsOpen(!isOpen);
  };

  const markAsRead = async (id) => {
    try {
      await notificationsApi.markAsRead(id);
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteAll = async () => {
    if (!window.confirm('Supprimer toutes vos notifications ? Cette action est irréversible.')) return;
    try {
      await notificationsApi.deleteAll();
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error deleting all notifications:', error);
    }
  };

  const deleteNotification = async (id, e) => {
    e.stopPropagation(); // Prevent navigation when clicking delete
    try {
      await notificationsApi.delete(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      // Update unread count if the deleted notification was unread
      const deletedNotif = notifications.find(n => n.id === id);
      if (deletedNotif && !deletedNotif.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read — a grouped notification acknowledges every child at once
    // so the badge count drops by the full group size instead of 1.
    const ids = notification.grouped_ids && notification.grouped_ids.length
      ? notification.grouped_ids
      : [notification.id];
    const unreadCountInGroup = notification.grouped_ids && notification.grouped_ids.length
      ? notification.grouped_count || ids.length
      : (!notification.is_read ? 1 : 0);
    if (unreadCountInGroup > 0) {
      try {
        if (ids.length > 1) {
          await notificationsApi.markBatchRead(ids);
        } else {
          await notificationsApi.markAsRead(ids[0]);
        }
        setNotifications(prev => prev.map(n =>
          ids.includes(n.id) ? { ...n, is_read: true } : n
        ));
        setUnreadCount(prev => Math.max(0, prev - unreadCountInGroup));
      } catch (error) {
        console.error('Error marking notification(s) as read:', error);
      }
    }
    
    // Close dropdown
    setIsOpen(false);
    
    // Navigate to the link if provided
    if (notification.link) {
      // Get current path to determine context (company/admin/client)
      const currentPath = window.location.pathname;
      let targetPath = notification.link;
      
      // If link is a relative path (not starting with /), it's already relative to current dashboard
      // If link starts with /, we need to check if it should be relative to the current dashboard
      if (targetPath.startsWith('/')) {
        // Check if we're in a dashboard context - handle both /company and /company/
        const isCompanyDashboard = currentPath === '/company' || currentPath.startsWith('/company/');
        const isAdminDashboard = currentPath === '/admin' || currentPath.startsWith('/admin/');
        
        if (isCompanyDashboard && !targetPath.startsWith('/company')) {
          // Convert absolute paths to company-relative paths
          // Map common paths to company dashboard routes
          const pathMappings = {
            '/reservations': '/company/punctual',
            '/abonnements': '/company/requests',
            '/clients': '/company/clients',
            '/planning': '/company/planning',
            '/interventions': '/company/interventions',
            '/collaborateurs': '/company/collaborators',
            '/services': '/company/services',
            '/avis': '/company/reviews'
          };
          targetPath = pathMappings[targetPath] || `/company${targetPath}`;
        } else if (isAdminDashboard && !targetPath.startsWith('/admin')) {
          // Convert absolute paths to admin-relative paths
          const adminPathMappings = {
            '/companies': '/admin/companies'
          };
          targetPath = adminPathMappings[targetPath] || `/admin${targetPath}`;
        }
      }
      
      navigate(targetPath);
    }
  };

  const handleMarkAsRead = async (id, e) => {
    e.stopPropagation(); // Prevent navigation when clicking mark as read
    await markAsRead(id);
  };

  const getIcon = (type) => {
    switch (type) {
      case 'reservation':
        return <Calendar className="w-4 h-4 text-blue-500" />;
      case 'payment':
        return <CreditCard className="w-4 h-4 text-green-500" />;
      case 'alert':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'system':
        return <Settings className="w-4 h-4 text-gray-500" />;
      default:
        return <Info className="w-4 h-4 text-primary" />;
    }
  };

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: fr });
    } catch {
      return '';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={handleBellClick}
        className="relative p-2 rounded-full hover:bg-muted transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span 
            className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
            data-testid="notification-count"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-background border rounded-lg shadow-lg z-50 max-h-[70vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b bg-muted/30">
            <h3 className="font-semibold text-sm">Notifications</h3>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSound}
                className="text-xs h-7 px-2"
                title={soundEnabled ? 'Désactiver le son' : 'Activer le son'}
                data-testid="notif-sound-toggle"
              >
                {soundEnabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5 text-muted-foreground" />}
              </Button>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-xs h-7 px-2"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="w-3 h-3 mr-1" />
                  Tout lire
                </Button>
              )}
              {notifications.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={deleteAll}
                  className="text-xs h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="delete-all-btn"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Tout supprimer
                </Button>
              )}
              <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-muted rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-4 text-center text-muted-foreground">
                Chargement...
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Aucune notification</p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map((notification) => {
                  const isGroup = (notification.grouped_count || 0) > 1;
                  const isExpanded = !!expandedGroups[notification.id];
                  return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-3 hover:bg-muted/50 transition-colors cursor-pointer ${
                      !notification.is_read ? 'bg-primary/5' : ''
                    } ${notification.link ? 'hover:bg-primary/10' : ''}`}
                    data-testid={`notification-item-${notification.id}`}
                  >
                    <div className="flex gap-3">
                      {/* Icon */}
                      <div className="flex-shrink-0 mt-0.5 relative">
                        {getIcon(notification.type)}
                        {isGroup && (
                          <span
                            className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-[10px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1 ring-2 ring-background"
                            data-testid={`notification-group-badge-${notification.id}`}
                          >
                            {notification.grouped_count > 9 ? '9+' : notification.grouped_count}
                          </span>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm ${!notification.is_read ? 'font-medium' : ''}`}>
                            {notification.title}
                          </p>
                          {!notification.is_read && (
                            <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                        {isGroup && (
                          <button
                            onClick={(e) => toggleGroupExpanded(notification.id, e)}
                            className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                            data-testid={`notification-group-toggle-${notification.id}`}
                          >
                            <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            {isExpanded ? 'Masquer le détail' : `Voir les ${notification.grouped_count} demandes`}
                          </button>
                        )}
                        {isGroup && isExpanded && (
                          <ul className="mt-2 space-y-1 border-l-2 border-primary/30 pl-2" data-testid={`notification-group-list-${notification.id}`}>
                            {(notification.grouped_titles || []).map((t, i) => (
                              <li key={i} className="text-[11px] text-muted-foreground line-clamp-1">
                                • {t}
                              </li>
                            ))}
                            {notification.grouped_count > (notification.grouped_titles?.length || 0) && (
                              <li className="text-[11px] text-muted-foreground italic">
                                + {notification.grouped_count - notification.grouped_titles.length} autre(s)…
                              </li>
                            )}
                          </ul>
                        )}
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {formatTime(notification.created_at)}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex-shrink-0 flex flex-col gap-1">
                        {!notification.is_read && (
                          <button
                            onClick={(e) => handleMarkAsRead(notification.id, e)}
                            className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground"
                            title="Marquer comme lu"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={(e) => deleteNotification(notification.id, e)}
                          className="p-1 hover:bg-red-100 rounded text-muted-foreground hover:text-red-500"
                          title="Supprimer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );})}
              </div>
            )}
          </div>

          {/* Footer link to full page */}
          {notifications.length > 0 && (
            <div className="border-t p-2 bg-muted/20">
              <button
                onClick={() => {
                  setIsOpen(false);
                  const currentPath = window.location.pathname;
                  const isCompany = currentPath === '/company' || currentPath.startsWith('/company/');
                  const isAdmin = currentPath === '/admin' || currentPath.startsWith('/admin/');
                  const isCollab = currentPath.startsWith('/collaborator');
                  const isClient = currentPath.startsWith('/client');
                  let target = '/notifications';
                  if (isCompany) target = '/company/notifications';
                  else if (isAdmin) target = '/admin/notifications';
                  else if (isCollab) target = '/collaborator/notifications';
                  else if (isClient) target = '/client/notifications';
                  navigate(target);
                }}
                className="w-full text-center text-xs text-primary hover:underline py-1.5"
                data-testid="view-all-notifications-btn"
              >
                Voir toutes les notifications →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
